import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { WebsocketService } from './websocket.service';

import { Message, MessageReadReceipt } from '../models/message.model';

export interface SendMessagePayload {
  conversation_id: number;
  sender_id: number;
  content: string;
}

export interface TypingPayload {
  conversation_id: number;
  sender_id: number;
  recipient_id: number;
  isTyping: boolean;
}

export interface ReadReceiptPayload {
  message_id: number;
  user_id: number;
  sender_id: number;
}

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  private onlineUserIdsSubject = new BehaviorSubject<Set<number>>(new Set());
  onlineUserIds$ = this.onlineUserIdsSubject.asObservable();

  constructor(private wsService: WebsocketService) {}

  connect(userId: number): void {
    this.wsService.connect(userId);

    setTimeout(() => {
      this.wsService.identify(userId);
    }, 500);
  }

  sendMessage(payload: SendMessagePayload): void {
    this.wsService.emit('message:send', payload);
  }

  editMessage(messageId: number, conversationId: number, newContent: string): void {
    this.wsService.emit('message:edit', {
      message_id: messageId,
      conversation_id: conversationId,
      new_content: newContent,
    });
  }

  deleteMessage(messageId: number, conversationId: number): void {
    this.wsService.emit('message:delete', {
      message_id: messageId,
      conversation_id: conversationId,
    });
  }

  sendTyping(payload: TypingPayload): void {
    this.wsService.emit('typing', payload);
  }

  onMessageRead() {
    return this.wsService.on<{
      conversation_id: number;
      receipts: MessageReadReceipt[];
    }>('message:read');
  }

  markDelivered(messageId: number, senderId: number): void {
    this.wsService.emit('message:delivered', {
      message_id: messageId,
      sender_id: senderId,
    });
  }

  onMessageStatus(): Observable<any> {
    return this.wsService.on('message:status');
  }

  archiveMessage(messageId: number, senderId: number, content: string): void {
    this.wsService.emit('message:archive', {
      original_message_id: messageId,
      sender_id: senderId,
      content,
    });
  }

  joinConversation(conversationId: number): void {
    this.wsService.emit('conversation:join', conversationId);
  }

  leaveConversation(conversationId: number): void {
    this.wsService.emit('conversation:leave', conversationId);
  }

  /**
   * Tells the server which conversation (if any) is actually on this
   * client's screen right now — separate from conversation:join/leave,
   * which track a much longer-lived "has this been selected at all" state
   * (deliberately kept across page navigation so the floating widget can
   * still show the same conversation later). This narrower signal is what
   * lets the server correctly skip a message notification only when the
   * recipient is truly looking at that conversation, not merely because
   * they opened it at some earlier point this session.
   */
  setVisibleConversation(conversationId: number | null): void {
    this.wsService.emit('conversation:visible', conversationId);
  }

  onConversationCreated(): Observable<{ conversation_id: number }> {
    return this.wsService.on<{ conversation_id: number }>('conversation:created');
  }

  onMessage(): Observable<Message> {
    return this.wsService.on<Message>('message:receive');
  }

  onMessageEdited(): Observable<{
    message_id: number;
    conversation_id: number;
    content: string;
    original_content?: string | null;
    is_edited: boolean;
    edited_at?: Date | string | null;
  }> {
    return this.wsService.on<{
      message_id: number;
      conversation_id: number;
      content: string;
      original_content?: string | null;
      is_edited: boolean;
      edited_at?: Date | string | null;
    }>('message:edited');
  }

  onReconnect(): Observable<boolean> {
    return this.wsService.onReconnect();
  }

  onTyping(): Observable<TypingPayload> {
    return this.wsService.on<TypingPayload>('typing');
  }

  onUserStatus(): Observable<{ userId: number; online: boolean }> {
    return this.wsService.on<{ userId: number; online: boolean }>('user:status');
  }

  onMessageDelivered(): Observable<{
    message_id: number;
    delivered_at?: Date | string | null;
  }> {
    return this.wsService.on<{
      message_id: number;
      delivered_at?: Date | string | null;
    }>('message:delivered');
  }

  onReadReceipt(): Observable<MessageReadReceipt> {
    return this.wsService.on<MessageReadReceipt>('message:read');
  }

  onMessageDeleted(): Observable<{
    message_id: number;
    conversation_id: number;
    deleted_at?: Date | string;
  }> {
    return this.wsService.on<{
      message_id: number;
      conversation_id: number;
      deleted_at?: Date | string;
    }>('message:deleted');
  }

  onConversationDisabled(): Observable<{ conversation_id: number }> {
    return this.wsService.on<{ conversation_id: number }>('conversation:disabled');
  }

  onConversationEnabled(): Observable<{ conversation_id: number }> {
    return this.wsService.on<{ conversation_id: number }>('conversation:enabled');
  }

  onConversationUpdated(): Observable<{
    conversation_id: number;
    lastMessage: string;
    time: Date | string;
    sender_id: number;
  }> {
    return this.wsService.on<{
      conversation_id: number;
      lastMessage: string;
      time: Date | string;
      sender_id: number;
    }>('conversation:updated');
  }

  onUserOnline(): Observable<{ user_id: number }> {
    return this.wsService.on<{ user_id: number }>('user:online');
  }

  onUserOffline(): Observable<{ user_id: number }> {
    return this.wsService.on<{ user_id: number }>('user:offline');
  }

  onPresenceList(): Observable<{ user_ids: number[] }> {
    return this.wsService.on<{ user_ids: number[] }>(`presence:list`);
  }

  setOnlineUsers(userIds: number[]): void {
    this.onlineUserIdsSubject.next(new Set(userIds.map((id) => Number(id))));
  }

  addOnlineUser(userId: number): void {
    const current = new Set(this.onlineUserIdsSubject.value);

    current.add(Number(userId));

    this.onlineUserIdsSubject.next(current);
  }

  removeOnlineUser(userId: number): void {
    const current = new Set(this.onlineUserIdsSubject.value);

    current.delete(Number(userId));

    this.onlineUserIdsSubject.next(current);
  }

  requestPresenceList(): void {
    this.wsService.emit('presence:get', {});
  }

  onMembersUpdated(): Observable<{
    conversation_id: number;
    left_user_id?: number;
    added_members?: { id: number; name: string }[];
  }> {
    return this.wsService.on<{
      conversation_id: number;
      left_user_id?: number;
      added_members?: { id: number; name: string }[];
    }>('conversation:membersUpdated');
  }

  onMessageError(): Observable<{ error: string }> {
    return this.wsService.on<{ error: string }>('message:error');
  }

  onConversationRenamed(): Observable<{ conversation_id: number; name: string }> {
    return this.wsService.on<{ conversation_id: number; name: string }>('conversation:renamed');
  }
}
